// Configuración de la API
import { getFirebaseToken } from "../authService";

const API_CONFIG = {
  // URL base del backend - ajusta según tu configuración
  BASE_URL: process.env.REACT_APP_API_URL || "http://localhost:3001",
  
  // Headers por defecto
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
  
  // Endpoints
  ENDPOINTS: {
    EMPRESAS: '/empresas',
    USUARIOS_EMPRESA: (empresaId) => `/usuarios-empresa/${empresaId}`,
    DATOS_USUARIO: '/datos-usuario',
    DATOS_USUARIO_ID: (userId) => `/datos-usuario-id/${userId}`,
    PUSH_TO_EMPRESA: '/push/to-empresa',
    PUSH_TO_USER: '/push/to-user',
    PUSH_TO_USERS: '/push/to-users',
    PUSH_LOGS: '/push/logs',
    PUSH_LOGS_SUMMARY: '/push/logs/summary',
    VALIDAR_CUENTA: '/validar-cuenta',
    LOGIN_LOGS: '/login-logs',
    LOGIN_STATS: '/login-stats',
    USUARIOS_CREDENCIALES_PENDIENTES: '/usuarios-credenciales-pendientes',
    MARCAR_CREDENCIALES_ENVIADAS: (userId) => `/marcar-credenciales-enviadas/${userId}`,
    ELIMINAR_SOLICITUD_ENVIO_CREDENCIALES: (id) => `/eliminar-solicitud-envio-credenciales/${id}`,
    DEVICES: '/devices',
    CURRENT_DEVICES: '/current-devices',
    OLDER_DEVICES: '/older-devices',
  },
  
  // Función para obtener headers con autenticación (síncrona, usa localStorage como fallback)
  getAuthHeaders: () => {
    const token = localStorage.getItem("adminJWT");
    return {
      ...API_CONFIG.DEFAULT_HEADERS,
      Authorization: token ? `Bearer ${token}` : '',
    };
  },

  // Función para obtener headers con autenticación (asíncrona, usa Firebase)
  getAuthHeadersAsync: async () => {
    const token = await getFirebaseToken();
    return {
      ...API_CONFIG.DEFAULT_HEADERS,
      Authorization: token ? `Bearer ${token}` : '',
    };
  },
  
  // Función para construir URLs completas
  buildUrl: (endpoint) => `${API_CONFIG.BASE_URL}${endpoint}`,
};

/**
 * Función de utilidad para hacer requests autenticados con Firebase
 * Obtiene automáticamente el token de Firebase antes de cada request
 * 
 * @param {string} endpoint - El endpoint de la API (ej: '/datos-usuario')
 * @param {object} options - Opciones de fetch (method, body, headers, etc.)
 * @returns {Promise<Response>} - La respuesta del fetch
 */
export async function apiRequest(endpoint, options = {}) {
  const token = await getFirebaseToken();
  
  if (!token) {
    throw new Error("No se pudo obtener el token de autenticación");
  }
  
  const url = endpoint.startsWith('http') ? endpoint : API_CONFIG.buildUrl(endpoint);
  const headers = {
    ...API_CONFIG.DEFAULT_HEADERS,
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };
  
  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Función wrapper para fetch que usa Firebase automáticamente
 * Reemplaza las llamadas a fetch directas en los componentes
 * 
 * @param {string} url - URL completa o endpoint
 * @param {object} options - Opciones de fetch
 * @returns {Promise<Response>}
 */
export async function fetchWithAuth(url, options = {}) {
  const token = await getFirebaseToken();
  
  if (!token) {
    throw new Error("No se pudo obtener el token de autenticación");
  }
  
  const headers = {
    ...API_CONFIG.DEFAULT_HEADERS,
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };
  
  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Helper para obtener el token de Firebase y verificar autenticación
 * Útil para reemplazar las verificaciones de localStorage.getItem('adminJWT')
 * 
 * @returns {Promise<string|null>} - El token o null si no está autenticado
 */
export async function getAuthToken() {
  return await getFirebaseToken();
}

export default API_CONFIG;
